import React, { useState } from "react";
import { useTranslation } from "react-i18next";
import downloadIcon from "../../assets/dashboard/download-icon.svg";
import reportDownloadIcon from "../../assets/dashboard/reportDownload-icon.svg";
import infoIcon from "../../assets/dashboard/info-icon.svg";
import successIcon from "../../assets/agent/success.svg";
import excel from "../../assets/dashboard/excel.svg";
import Button from "./Button";

const DownloadReport = ({ className = "", disabled = false, agents = [] }) => {
  const { t } = useTranslation();
  const [showModal, setShowModal] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isComplete, setIsComplete] = useState(false);
  const [isSaving, setIsSaving] = useState(false);

  const handleDownload = () => {
    setShowModal(true);
    setProgress(0);
    setIsComplete(false);
    // Simulate progress
    let prog = 0;
    const interval = setInterval(() => {
      prog += Math.floor(Math.random() * 15) + 10;
      if (prog >= 100) {
        prog = 100;
        setProgress(prog);
        setIsComplete(true);
        clearInterval(interval);
      } else {
        setProgress(prog);
      }
    }, 400);
  };

  const handleClose = () => {
    setShowModal(false);
    setProgress(0);
    setIsComplete(false);
    setIsSaving(false);
  };

  const handleSave = () => {
    setIsSaving(true);
    setTimeout(() => {
      const headers = [
        "Agent",
        "Company",
        "Total Calls",
        "Answered",
        "Leads",
        "Meetings",
        "Out of Service",
        "Success",
      ];

      const rows = agents.map((agent) => [
        agent.agent_name ?? "-",
        agent.company_name ?? "-",
        agent.total_calls_made ?? "-",
        agent.total_calls_answered ?? "-",
        agent.total_leads ?? "-",
        agent.total_meetings_booked ?? "-",
        agent.total_discarded ?? "-",
        agent.total_meetings_confirmed_attended ?? "-",
      ]);

      let csvContent =
        headers.join(",") +
        "\n" +
        rows.map((row) => row.map((val) => `"${val}"`).join(",")).join("\n");

      // Trigger download
      const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });
      const link = document.createElement("a");
      link.href = URL.createObjectURL(blob);
      link.download = "agents_report.csv";
      link.click();

      setIsSaving(false);
      handleClose();
    }, 1200);
  };

  return (
    <>
      <div className="relative">
        <div className="flex items-center gap-2">
          <button
            onClick={handleDownload}
            disabled={disabled}
            className={`group w-14 h-9 border border-secondary rounded-md flex items-center justify-center hover:bg-[linear-gradient(-80deg,#136BF0_0%,#0C49A6_100%)] transition-colors duration-200 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 ${
              disabled ? "opacity-50 cursor-not-allowed" : "cursor-pointer"
            } ${className}`}
          >
            <img
              src={downloadIcon}
              alt="Download"
              className="w-4 h-4 transition duration-300 group-hover:brightness-0 group-hover:invert"
            />
          </button>
          <div className="group">
            <img
              src={infoIcon}
              alt="info"
              className="w-4 h-4 cursor-pointer relative"
            />
            <div className="absolute left-12 top-1/2 -translate-y-14 w-[150px] px-2 py-1 bg-white text-primary text-xs rounded shadow-lg opacity-0 group-hover:opacity-100 transition-opacity duration-200 z-10">
              {t("dashboard.tooltip.download")}
            </div>
          </div>
        </div>
      </div>
      {showModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-[#000] bg-opacity-30 px-4">
          <div className="bg-white rounded-2xl shadow-xl w-full max-w-[655px] h-[340px] p-3 flex flex-col items-center relative">
            {isComplete ? (
              <>
                <div className="flex flex-col items-center w-full">
                  <div className="mb-5 mt-2">
                    <img
                      src={successIcon}
                      alt="Success"
                      className="w-16 h-16"
                    />
                  </div>
                  <div className="text-xl font-semibold text-primary mb-4 text-center">
                    {t("dashboard.reportDownload.readyReport")}
                  </div>
                  <div className="relative flex items-center">
                    <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
                    <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[348px]" />
                    <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
                  </div>
                  <div className="w-full flex flex-col items-center mt-9 mb-4">
                    <div className="relative bg-gray-100 rounded-xl px-5 py-2 w-full flex items-center shadow-sm">
                      <img src={excel} alt="excel" className="w-7 h-7 mr-3" />
                      <div className="flex-1 min-w-0">
                        <div className="font-semibold text-[15px] truncate">
                          Data Example
                        </div>
                        <div className="text-xs text-gray-500">0mb</div>
                        <div className="w-full flex items-center mt-2">
                          <div className="flex-1 h-2 bg-gray-300 rounded-full relative overflow-hidden">
                            <div
                              className="bg-[#136BF0] h-2 rounded-full transition-all duration-300"
                              style={{ width: `100%` }}
                            ></div>
                          </div>
                          <div
                            className="ml-2 text-xs text-gray-500 font-medium"
                            style={{ minWidth: 32 }}
                          >
                            100%
                          </div>
                        </div>
                      </div>
                      <button
                        className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-lg font-bold focus:outline-none"
                        onClick={handleClose}
                        aria-label="Close"
                      >
                        ×
                      </button>
                    </div>
                  </div>
                  <Button
                    variant="primary"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving
                      ? t("dashboard.reportDownload.saving")
                      : t("dashboard.reportDownload.saveFile")}
                  </Button>
                </div>
              </>
            ) : (
              <div className="flex flex-col items-center w-full">
                <div className="mb-6 mt-2">
                  <div className="bg-secondary rounded-full w-16 h-16 flex items-center justify-center shadow-md">
                    <img
                      src={reportDownloadIcon}
                      alt="reportDownloadIcon"
                      className="w-8 h-8"
                    />
                  </div>
                </div>
                <div className="text-xl font-semibold text-primary mb-8 text-center">
                  {t("dashboard.reportDownload.preparing")}
                </div>
                <div className="relative flex items-center">
                  <span className="absolute left-0 w-1 h-1 bg-[#0A1E4C] rounded-full -ml-1"></span>
                  <hr className="flex-1 border-t-2 border-[#0A1E4C] w-[348px]" />
                  <span className="absolute right-0 w-1 h-1 bg-[#0A1E4C] rounded-full -mr-1"></span>
                </div>
                {/* Progress bar section */}
                <div className="w-full flex flex-col items-center mt-9">
                  <div className="relative bg-gray-100 rounded-xl px-5 py-2 w-full flex items-center shadow-sm">
                    <img src={excel} alt="excel" className="w-7 h-7 mr-3" />
                    <div className="flex-1 min-w-0">
                      <div className="font-semibold text-[15px] truncate">
                        Data Example
                      </div>
                      <div className="text-xs text-gray-500">0mb</div>
                      <div className="w-full flex items-center mt-2">
                        <div className="flex-1 h-2 bg-gray-300 rounded-full relative overflow-hidden">
                          <div
                            className="bg-[#136BF0] h-2 rounded-full transition-all duration-300"
                            style={{ width: `${progress}%` }}
                          ></div>
                        </div>
                        <div
                          className="ml-2 text-xs text-gray-500 font-medium"
                          style={{ minWidth: 32 }}
                        >
                          {progress}%
                        </div>
                      </div>
                    </div>
                    <button
                      className="absolute top-2 right-3 text-gray-400 hover:text-gray-600 text-lg font-bold focus:outline-none"
                      onClick={handleClose}
                      aria-label="Close"
                    >
                      ×
                    </button>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </>
  );
};

export default DownloadReport;
